/**
 * 雜項費用（v2.29.0）—— 會計填寫介面
 *
 * 嵌在「營收數據 › 雜項費用」Tab 裡（Revenue.js 的 activeProvider === 'MiscFee' 分支）。
 *
 * 取代原本從「※ 營利表」Google Sheet 爬回來的唯讀表格。掃過全部週期、
 * 區分公式與人工輸入之後，實際上只有這幾格是**人在填**的：
 *
 *     人事成本  信用卡雜項  公費報帳   ← 這一頁
 *     台幣幣商佣金  幣商實際交收        ← 在「金流數據」那一頁（跟金流量擺一起才看得懂）
 *
 * ⚠️ 依 Brad 的原則「他想填自然就會填」：
 *   · 不設「月結 / 週結」屬性 —— 信用卡雜項與人事成本實際上多為月結
 *     （`54649 7月` 填在 8/3-8/9 那一週），但哪天有特殊人事需求隔四個月才填也不意外
 *   · **不做必填星號、不跳「尚未填寫」提醒**。空的就是空的，不是遺漏
 *   · 沒填 = 0（人工費用沒填就是真的沒有，不同於爬來的數據要區分 0 與無資料）
 *
 * 常用項目一律列出但留空，方便會計找得到；其餘走「＋ 新增項目」。
 *
 * 週期清單（`weeks`，由新到舊）由 Revenue.js 的 getMiscFeeWeeks 給，
 * **跟總淨營收同一份**：pipeline 跑完哪一週，這裡就有哪一週的格子。
 * 這裡只再砍掉「比最早一筆登記還舊」的空白週 —— 那段是 Google Sheet 的年代、
 * 後臺本來就沒有資料，列出來只是一長串空白。
 * ⚠️ 比最新一筆登記還新的週**不砍**，那正是留給會計填的。
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Nav, NavItem, NavLink, Table } from 'reactstrap';
import classNames from 'classnames';
import { Bar } from 'react-chartjs-2';
import CashflowEditCell from 'components/tiny/CashflowEditCell';
import TinyLoading, { TinyLoadingOverlay } from 'components/tiny/TinyLoading';

const C = {
  border: 'rgba(255,255,255,0.1)',
  inputBg: '#1a1f2b',
  text: '#fff',
  sub: 'rgba(255,255,255,0.8)',
  dim: 'rgba(255,255,255,0.4)',
  success: '#2dce89',
  warning: '#fb6340',
};

const TH = (bg) => ({ backgroundColor: bg, textAlign: 'center', fontSize: '15px', whiteSpace: 'nowrap' });
const TD = { textAlign: 'center', fontSize: '14px', whiteSpace: 'nowrap' };

const fmt = (n) => (n === null || n === undefined || Number.isNaN(Number(n))
  ? '—' : Math.round(Number(n)).toLocaleString('en-US'));

const label = (w) => (/^\d{8}-\d{8}$/.test(w)
  ? `${w.slice(4, 6)}/${w.slice(6, 8)}–${w.slice(13, 15)}/${w.slice(15, 17)}` : w);

// 這一頁管的項目。金流相關的（佣金 / 實際交收）刻意留在「金流數據」頁，
// 跟金流量擺一起才對得起來。
const ITEMS = [
  { key: 'personnel_cost', label: '人事成本', bg: 'rgba(245,54,92,0.2)' },
  { key: 'credit_card_misc', label: '信用卡雜項', bg: 'rgba(153,102,255,0.2)' },
  { key: 'public_expense', label: '公費報帳', bg: 'rgba(255,194,86,0.2)' },
];

// 跟金流數據頁同一套：檢視切換用主 accent 靛藍
const ACCENT_VIEW = '102,126,234';

const Seg = ({ options, value, onChange, accent = ACCENT_VIEW }) => (
  <Nav pills className="rev-seg-nav" style={{ '--tab-accent': accent }}>
    {options.map((o) => (
      <NavItem key={o.key}>
        <NavLink href="#" className={classNames('rev-seg-link', { active: value === o.key })}
          onClick={(e) => { e.preventDefault(); onChange(o.key); }}>
          {o.icon && <i className={`fa ${o.icon} mr-1`} />}{o.label}
        </NavLink>
      </NavItem>
    ))}
  </Nav>
);

export default function TinyMiscFee({ weeks = [] }) {
  const [view, setView] = useState('table');
  const [rows, setRows] = useState({});      // {week: {item_key: {amount, note}}}
  const [custom, setCustom] = useState({});  // {week: [entry]}
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null); // week
  const [draft, setDraft] = useState({ label: '', amount: '', note: '' });

  const load = useCallback(async () => {
    if (!weeks.length) return;
    setLoading(true);
    try {
      // ⚠️ 一個 request 就好。原本是 `weeks.map(w => axios.get(...))` 一週一個 ——
      // Sheet 有 210 列週期就是 210 個請求，而 `gunicorn -w 2` 一次只吃 2 個，
      // 等於 105 輪往返，開這一頁要等 2~3 秒。瓶頸是往返次數不是 Mongo
      //（每個查詢都走索引、都是 1ms 級），而且每個請求還各自重讀一次
      // `cashflow_editors` 只為拿同一個布林值。
      const sorted = [...weeks].sort();
      const res = await axios.get('/api/tiny/cashflow/manual/bulk'
        + `?from=${sorted[0]}&to=${sorted[sorted.length - 1]}`);
      const d = res.data?.data || {};
      const map = {};
      const cus = {};
      // 完全沒有登記的週期不會出現在回傳裡，缺席就好 ——
      // 讀取端一律走 `rows[w] || {}`，不需要補空殼。
      Object.entries(d.weeks || {}).forEach(([w, wd]) => {
        map[w] = Object.fromEntries((wd.common || []).map((it) => [it.item_key, it]));
        cus[w] = wd.custom || [];
      });
      setRows(map); setCustom(cus); setCanWrite(!!d.can_write);
    } finally { setLoading(false); }
  }, [weeks]);

  useEffect(() => { load(); }, [load]);

  const save = async (week, itemKey, itemLabel, amount, note) => {
    await axios.post('/api/tiny/cashflow/manual', {
      week, item_key: itemKey, item_label: itemLabel, amount, note,
    });
    await load();
  };

  const addCustom = async (week) => {
    if (!draft.label.trim() || draft.amount === '') { setAdding(null); return; }
    const key = `custom:${Date.now().toString(36)}`;
    await save(week, key, draft.label.trim(), Number(draft.amount), draft.note || null);
    setAdding(null); setDraft({ label: '', amount: '', note: '' });
  };

  if (loading && !Object.keys(rows).length) {
    return <TinyLoading variant="block" text="載入中" />;
  }

  // 砍掉比最早一筆登記還舊的空白週（見檔頭）。完全沒有登記時全部列出。
  const earliest = [...Object.keys(rows), ...Object.keys(custom)]
    .filter((w) => (rows[w] && Object.keys(rows[w]).length) || (custom[w] || []).length)
    .sort()[0];
  const weeksShown = earliest ? weeks.filter((w) => w >= earliest) : weeks;

  // 只有四類費用，堆疊長條圖剛好 —— 一眼看出每週總支出與組成
  const shown = [...weeksShown].filter((w) => {
    const r = rows[w] || {};
    return ITEMS.some((it) => r[it.key]?.amount) || (custom[w] || []).length;
  }).reverse();
  const chartData = {
    labels: shown.map(label),
    datasets: [
      ...ITEMS.map((it) => ({
        label: it.label,
        backgroundColor: it.bg.replace('0.2', '0.85'),
        data: shown.map((w) => (rows[w] || {})[it.key]?.amount || 0),
        stack: 'misc', borderRadius: 2,
      })),
      {
        label: '其他項目', backgroundColor: 'rgba(17,205,239,0.85)', stack: 'misc',
        data: shown.map((w) => (custom[w] || []).reduce((a, c) => a + (c.amount || 0), 0)),
        borderRadius: 2,
      },
    ],
  };
  const chartOpts = {
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: C.sub, boxWidth: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmt(c.parsed.y)}` } },
    },
    scales: {
      x: { stacked: true, ticks: { color: C.dim, font: { size: 10 }, maxRotation: 45 },
        grid: { color: 'rgba(255,255,255,0.04)' } },
      y: { stacked: true, beginAtZero: true,
        ticks: { color: C.dim, font: { size: 10 },
          callback: (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M`
            : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v) },
        grid: { color: 'rgba(255,255,255,0.06)' } },
    },
  };

  if (view === 'chart') {
    return (
      <div>
        <Seg accent={ACCENT_VIEW} value={view} onChange={setView}
          options={[{ key: 'table', label: '表格', icon: 'fa-th-large' },
            { key: 'chart', label: '圖表', icon: 'fa-bar-chart' }]} />
        <div style={{ fontSize: 12, color: C.dim, margin: '10px 0 6px' }}>
          <i className="fa fa-info-circle mr-1" />
          只列有支出的期間（{shown.length} 期）；同一根柱子疊出當週總支出與組成。
        </div>
        <div style={{ height: 360 }}><Bar data={chartData} options={chartOpts} /></div>
      </div>
    );
  }

  return (
    <TinyLoadingOverlay active={loading} text="更新中">
    <div className="table-responsive">
      <Seg accent={ACCENT_VIEW} value={view} onChange={setView}
        options={[{ key: 'table', label: '表格', icon: 'fa-th-large' },
          { key: 'chart', label: '圖表', icon: 'fa-bar-chart' }]} />
      <div style={{ fontSize: 12, color: C.dim, margin: '10px 0 8px' }}>
        <i className="fa fa-info-circle mr-1" />
        有就填，沒有就不用管 —— 不用每週都填、也沒有規定什麼時候填。
        空白代表沒有這筆費用。
      </div>
      <Table className="tablesorter">
        <thead className="text-primary">
          <tr>
            <th style={{ ...TH('transparent'), width: 170 }}>期間</th>
            {ITEMS.map((it) => (
              <th key={it.key} style={TH(it.bg)}>{it.label}<br /><small>會計填寫</small></th>
            ))}
            <th style={TH('rgba(17,205,239,0.2)')}>其他項目<br /><small>可自行新增</small></th>
            <th style={{ ...TH('rgba(45,206,137,0.2)'), width: 130 }}>合計</th>
          </tr>
        </thead>
        <tbody>
          {weeksShown.map((w, i) => {
            const r = rows[w] || {};
            const y = w.slice(0, 4);
            const newYear = i === 0 || y !== weeksShown[i - 1].slice(0, 4);
            const cs = custom[w] || [];
            const total = ITEMS.reduce((a, it) => a + (r[it.key]?.amount || 0), 0)
              + cs.reduce((a, c) => a + (c.amount || 0), 0);
            return (
              <React.Fragment key={w}>
              {newYear && (
                <tr>
                  <td colSpan={ITEMS.length + 3} style={{ textAlign: 'center', padding: '6px',
                    background: 'rgba(255,255,255,0.05)', fontWeight: 600, letterSpacing: '.05em' }}>
                    {y} 年
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ ...TD, fontWeight: 600 }}>{label(w)}</td>
                {ITEMS.map((it) => (
                  <td key={it.key} style={TD}>
                    {/* withNote：這些項目實務上多是月結，會計會在備註寫「7月」之類，
                        所以備註也要能改，不能只改數字 */}
                    <CashflowEditCell canWrite={canWrite} withNote
                      value={r[it.key]?.amount} note={r[it.key]?.note}
                      onSave={(v, n) => save(w, it.key, it.label, v, n)} />
                  </td>
                ))}
                <td style={{ ...TD, whiteSpace: 'normal', textAlign: 'left' }}>
                  {cs.map((c) => (
                    <div key={c.item_key} style={{ fontSize: 13 }}>
                      <span style={{ color: C.dim }}>{c.item_label}</span>{' '}
                      <CashflowEditCell canWrite={canWrite} withNote value={c.amount} note={c.note}
                        onSave={(v, n) => save(w, c.item_key, c.item_label, v, n)} />
                    </div>
                  ))}
                  {canWrite && (adding === w ? (
                    <div className="d-flex" style={{ gap: 4, marginTop: 4 }}>
                      <input placeholder="項目名稱" value={draft.label}
                        onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                        style={{ width: 110, background: C.inputBg, color: C.text,
                          border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 6px', fontSize: 12 }} />
                      <input placeholder="金額" value={draft.amount}
                        onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCustom(w); }}
                        style={{ width: 80, background: C.inputBg, color: C.text,
                          border: `1px solid ${C.border}`, borderRadius: 4, padding: '2px 6px',
                          fontSize: 12, textAlign: 'right' }} />
                      <button type="button" className="btn btn-sm btn-success py-1 px-3 m-0"
                        style={{ fontSize: 12 }} onClick={() => addCustom(w)}>儲存</button>
                      <button type="button" className="btn btn-sm btn-secondary py-1 px-3 m-0"
                        style={{ fontSize: 12 }} onClick={() => setAdding(null)}>取消</button>
                    </div>
                  ) : (
                    <span style={{ cursor: 'pointer', color: C.dim, fontSize: 12 }}
                      onClick={() => { setDraft({ label: '', amount: '', note: '' }); setAdding(w); }}>
                      ＋ 新增項目
                    </span>
                  ))}
                  {!canWrite && !cs.length && <span style={{ color: C.dim }}>—</span>}
                </td>
                <td style={{ ...TD, fontWeight: 600, color: total ? C.text : C.dim }}>
                  {total ? fmt(total) : '—'}
                </td>
              </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      {!canWrite && (
        <div style={{ fontSize: 12, color: C.dim, padding: '8px 4px' }}>
          <i className="fa fa-lock mr-1" />只有指定帳號可以編輯，其餘唯讀。
        </div>
      )}
    </div>
    </TinyLoadingOverlay>
  );
}
